import React, { useState, useEffect } from "react";
import {
  CreditCard,
  Receipt,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Search,
  Filter,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Download,
  Calendar,
  BookOpen,
  Loader,
  ArrowUpRight,
  ShieldCheck,
} from "lucide-react";
import {
  lmsGetStudentEnrolledCourses,
  lmsGetStudentPaymentHistory,
} from "../api/lmsApi";
import "../lms.css";

const getStudentData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const studentId = ud.student_id || ud.id || null;
    const schoolId = ud.school_id || ud.admin_id || ud.parent_id || null;
    return { studentId, schoolId, ...ud };
  } catch {
    return { studentId: null, schoolId: null };
  }
};

export default function StudentPaymentHistoryLedger() {
  const { studentId, schoolId } = getStudentData();

  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedCourses, setExpandedCourses] = useState({});

  useEffect(() => {
    fetchData();
  }, [studentId, schoolId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (studentId) {
        const [cRes, pRes] = await Promise.all([
          lmsGetStudentEnrolledCourses(studentId, schoolId),
          lmsGetStudentPaymentHistory(studentId, schoolId),
        ]);

        const cData = cRes.data?.data || (Array.isArray(cRes.data) ? cRes.data : []);
        const pData = pRes.data?.data || (Array.isArray(pRes.data) ? pRes.data : []);

        setCourses(cData);
        setPayments(pData);

        // Initially expand all courses
        const initialExpanded = {};
        cData.forEach((c) => {
          const cId = c.course_id || c.id;
          if (cId) initialExpanded[cId] = true;
        });
        setExpandedCourses(initialExpanded);
      }
    } catch (err) {
      console.error("Error fetching student payment ledger:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCourseExpand = (courseId) => {
    setExpandedCourses((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  };

  // Group payments by course_id
  const paymentsByCourse = payments.reduce((acc, pay) => {
    const cId = pay.course_id;
    if (!acc[cId]) acc[cId] = [];
    acc[cId].push(pay);
    return acc;
  }, {});

  // Calculate global summary stats
  const totalFees = courses.reduce(
    (sum, c) => sum + (parseFloat(c.course_discount_price || c.course_price || c.price || 0)),
    0
  );
  const totalPaid = courses.reduce(
    (sum, c) => sum + parseFloat(c.total_paid_amount || 0),
    0
  );
  const totalPending = Math.max(0, totalFees - totalPaid);

  // Filter courses
  const filteredCourses = courses.filter((course) => {
    const cName = (course.course_name || course.name || "").toLowerCase();
    const matchesSearch = cName.includes(searchTerm.toLowerCase());

    const pStatus = (course.enrollment_payment_status || "unpaid").toLowerCase();
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "paid" && (pStatus === "paid" || course.pending_amount <= 0)) ||
      (statusFilter === "partial" && pStatus === "partial") ||
      (statusFilter === "unpaid" && (pStatus === "unpaid" || pStatus === "pending"));

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const formatMethod = (method) => {
    if (!method) return "Cash";
    switch (method.toLowerCase()) {
      case "upi_qr":
      case "upi":
        return "UPI / QR Code";
      case "bank_transfer":
      case "bank":
        return "Bank Transfer";
      case "cash":
        return "Cash";
      default:
        return method.toUpperCase();
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <Loader size={32} className="spinner" style={{ margin: "0 auto 12px" }} />
        <p style={{ color: "#64748b", fontSize: "15px" }}>Loading payment history ledger...</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Top Ledger Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: "20px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "#eff6ff",
              color: "#1c498d",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BookOpen size={24} />
          </div>
          <div>
            <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "500" }}>
              Total Enrolled Courses
            </span>
            <h3 style={{ fontSize: "22px", fontWeight: "700", color: "#0f172a", margin: "2px 0 0" }}>
              {courses.length}
            </h3>
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: "20px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "#f0fdf4",
              color: "#16a34a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Receipt size={24} />
          </div>
          <div>
            <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "500" }}>
              Total Course Fees
            </span>
            <h3 style={{ fontSize: "22px", fontWeight: "700", color: "#0f172a", margin: "2px 0 0" }}>
              ₹{totalFees.toLocaleString("en-IN")}
            </h3>
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: "20px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "#ecfdf5",
              color: "#059669",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircle size={24} />
          </div>
          <div>
            <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "500" }}>
              Total Paid Amount
            </span>
            <h3 style={{ fontSize: "22px", fontWeight: "700", color: "#16a34a", margin: "2px 0 0" }}>
              ₹{totalPaid.toLocaleString("en-IN")}
            </h3>
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: "20px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: totalPending > 0 ? "#fff1f2" : "#f0fdf4",
              color: totalPending > 0 ? "#e11d48" : "#16a34a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Clock size={24} />
          </div>
          <div>
            <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "500" }}>
              Outstanding Balance
            </span>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: totalPending > 0 ? "#dc2626" : "#16a34a",
                margin: "2px 0 0",
              }}
            >
              ₹{totalPending.toLocaleString("en-IN")}
            </h3>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: "16px 20px",
          border: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1 1 280px" }}>
          <Search size={18} color="#94a3b8" />
          <input
            type="text"
            placeholder="Search by course name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              border: "none",
              outline: "none",
              fontSize: "14px",
              width: "100%",
              color: "#1e293b",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#64748b" }}>
            <Filter size={15} />
            <span>Filter Status:</span>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
              fontSize: "13px",
              color: "#334155",
              outline: "none",
              background: "#ffffff",
            }}
          >
            <option value="all">All Statuses</option>
            <option value="paid">Fully Paid</option>
            <option value="partial">Partially Paid</option>
            <option value="unpaid">Unpaid / Pending</option>
          </select>
        </div>
      </div>

      {/* Course-Wise Payment Ledger List */}
      {filteredCourses.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "50px 20px",
            background: "#ffffff",
            borderRadius: "12px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <Receipt size={44} color="#94a3b8" style={{ margin: "0 auto 12px" }} />
          <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#334155" }}>
            No payment records matching your filter
          </h4>
          <p style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>
            Check back after enrolling in courses or completing a fee payment.
          </p>
        </div>
      ) : (
        filteredCourses.map((course) => {
          const cId = course.course_id || course.id;
          const cName = course.course_name || course.name;
          const cPrice = parseFloat(course.course_discount_price || course.course_price || course.price || 0);
          const paidAmt = parseFloat(course.total_paid_amount || 0);
          const pendAmt = Math.max(0, cPrice - paidAmt);
          const coursePaymentList = paymentsByCourse[cId] || [];
          const isExpanded = expandedCourses[cId] !== false;

          const paymentStatus =
            pendAmt === 0 && cPrice > 0
              ? "paid"
              : paidAmt > 0
              ? "partial"
              : "unpaid";

          return (
            <div
              key={cId}
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                overflow: "hidden",
              }}
            >
              {/* Course Header Banner */}
              <div
                onClick={() => toggleCourseExpand(cId)}
                style={{
                  padding: "18px 24px",
                  background: "#f8fafc",
                  borderBottom: isExpanded ? "1px solid #e2e8f0" : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "8px",
                      background: "#1c498d",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "700",
                      fontSize: "16px",
                    }}
                  >
                    {cName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#0f172a", margin: 0 }}>
                      {cName}
                    </h3>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "4px" }}>
                      {course.instructor_name && (
                        <span style={{ fontSize: "12px", color: "#64748b" }}>
                          Instructor: {course.instructor_name}
                        </span>
                      )}
                      {course.class_name && (
                        <span style={{ fontSize: "12px", color: "#64748b" }}>
                          Class: {course.class_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                  {/* Financial Summary Pill */}
                  <div style={{ display: "flex", alignItems: "center", gap: "16px", textAlign: "right" }}>
                    <div>
                      <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", fontWeight: "600" }}>
                        Course Fee
                      </div>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: "#334155" }}>
                        ₹{cPrice.toLocaleString("en-IN")}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", fontWeight: "600" }}>
                        Paid
                      </div>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: "#16a34a" }}>
                        ₹{paidAmt.toLocaleString("en-IN")}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", fontWeight: "600" }}>
                        Pending
                      </div>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: pendAmt > 0 ? "#dc2626" : "#16a34a" }}>
                        ₹{pendAmt.toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div>
                    {paymentStatus === "paid" ? (
                      <span
                        style={{
                          background: "#dcfce7",
                          color: "#15803d",
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <CheckCircle size={13} /> Paid
                      </span>
                    ) : paymentStatus === "partial" ? (
                      <span
                        style={{
                          background: "#fef9c3",
                          color: "#a16207",
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Clock size={13} /> Partial
                      </span>
                    ) : (
                      <span
                        style={{
                          background: "#fee2e2",
                          color: "#b91c1c",
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <AlertCircle size={13} /> Unpaid
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#64748b",
                      cursor: "pointer",
                    }}
                  >
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                </div>
              </div>

              {/* Course Payment Ledger Table */}
              {isExpanded && (
                <div style={{ padding: "20px 24px" }}>
                  <h4
                    style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <FileText size={15} color="#1c498d" />
                    Transaction Ledger History
                  </h4>

                  {coursePaymentList.length === 0 ? (
                    <div
                      style={{
                        padding: "24px",
                        textAlign: "center",
                        background: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px dashed #cbd5e1",
                      }}
                    >
                      <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>
                        No payment transactions recorded for this course yet.
                      </p>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "13px",
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              borderBottom: "2px solid #e2e8f0",
                              background: "#f1f5f9",
                              textAlign: "left",
                              color: "#475569",
                              fontSize: "12px",
                            }}
                          >
                            <th style={{ padding: "10px 14px", fontWeight: "600" }}>#</th>
                            <th style={{ padding: "10px 14px", fontWeight: "600" }}>Date & Time</th>
                            <th style={{ padding: "10px 14px", fontWeight: "600" }}>Txn ID</th>
                            <th style={{ padding: "10px 14px", fontWeight: "600" }}>Mode</th>
                            <th style={{ padding: "10px 14px", fontWeight: "600" }}>Remarks</th>
                            <th style={{ padding: "10px 14px", fontWeight: "600", textAlign: "right" }}>
                              Amount Paid
                            </th>
                            <th style={{ padding: "10px 14px", fontWeight: "600", textAlign: "center" }}>
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {coursePaymentList.map((p, idx) => (
                            <tr
                              key={p.id || idx}
                              style={{
                                borderBottom: "1px solid #f1f5f9",
                                transition: "background 0.15s ease",
                              }}
                            >
                              <td style={{ padding: "12px 14px", color: "#64748b", fontWeight: "500" }}>
                                {idx + 1}
                              </td>
                              <td style={{ padding: "12px 14px", color: "#1e293b", fontWeight: "500" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <Calendar size={13} color="#64748b" />
                                  {formatDate(p.payment_date || p.created_at)}
                                </div>
                              </td>
                              <td style={{ padding: "12px 14px", fontFamily: "monospace", color: "#334155" }}>
                                {p.transaction_id || `TXN-${p.id}`}
                              </td>
                              <td style={{ padding: "12px 14px", color: "#334155" }}>
                                <span
                                  style={{
                                    background: "#f1f5f9",
                                    padding: "3px 8px",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {formatMethod(p.payment_method)}
                                </span>
                              </td>
                              <td style={{ padding: "12px 14px", color: "#64748b" }}>
                                {p.remarks || "Course Fee Payment"}
                              </td>
                              <td
                                style={{
                                  padding: "12px 14px",
                                  fontWeight: "700",
                                  color: "#16a34a",
                                  textAlign: "right",
                                  fontSize: "14px",
                                }}
                              >
                                ₹{parseFloat(p.amount || 0).toLocaleString("en-IN")}
                              </td>
                              <td style={{ padding: "12px 14px", textAlign: "center" }}>
                                <span
                                  style={{
                                    background: "#dcfce7",
                                    color: "#15803d",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "3px",
                                  }}
                                >
                                  <ShieldCheck size={12} /> Success
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
